import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Icon, Tag, Text, useToggle } from '@factorialco/deprecated-design-system'
import { ContractTaxonomyFragment } from 'generated/resources/graphql'
import type moment from 'moment'

import { observer } from 'mobx-react'

import { ExpiredTrialActionChecker } from 'modules/core/components/ExpiredTrialActionChecker'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { stringToDate } from 'modules/core/lib/dates'
import { employeeLegalEntity, employeeLegalEntityId } from 'modules/core/lib/employeeLegalEntity'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import type Employee from 'modules/employees/store/models/Employee'
import CompareText from 'modules/payroll_bundle/components/CompareText'
import type SupplementsFormStore from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/store'
import { EmployeeEstimatedPeriodBaseSalary } from 'modules/payroll_bundle/hooks/useEmployeeEstimatedPeriodBaseSalary'
import { Option } from 'modules/payroll_bundle/lib/constants'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import { useStatusAuthorizations } from 'modules/payroll_bundle/lib/statusAuthorizationsContext'
import { isSupplementFormInvalid } from 'modules/payroll_bundle/pages/SupplementsPage/SupplementsInsights'
import { useSupplementsInsights } from 'modules/payroll_bundle/pages/SupplementsPage/supplementsInsightsContext'
import observations from 'modules/payroll_bundle/store/collections/observations'

import EmployeeHeader from './EmployeeHeader'
import ObservationModal from './ObservationModal'
import SupplementCreateContent from './SupplementCreateContent'
import styles from './index.module.css'

interface RenderProps {
  isLast: boolean
  hover: boolean
}

interface Props {
  store: SupplementsFormStore
  taxonomies: ContractTaxonomyFragment[]
  employee: Employee
  estimatedSalary?: EmployeeEstimatedPeriodBaseSalary
  effectiveOn: moment.Moment
  loadingSupplements: boolean
  shouldScrollIntoView?: boolean | null
  isLast: boolean
  employeeExpansionState?: Record<string, boolean>
  toggleEmployeeExpansion?: (id: string) => void
  toggleTaxonomyCreationModal: (value: boolean) => void
}

const SupplementsItem = ({
  store,
  taxonomies,
  isLast,
  employee,
  estimatedSalary,
  children,
  effectiveOn,
  loadingSupplements,
  shouldScrollIntoView,
  employeeExpansionState,
  toggleEmployeeExpansion,
  toggleTaxonomyCreationModal,
}: Props & React.ConsumerProps<RenderProps>) => {
  const [hover, toggleHover] = useToggle(false) // Using this approach for performance reasons
  const [isSupplementCreationModalOpen, setIsSupplementCreationModalOpen] = React.useState(false)

  const { previousPeriodTotalByEmployee: totalForPreviousPeriod } = useSupplementsInsights()
  const [modalOpen, setModalOpen] = React.useState<boolean>(false)
  const [openPopover, setOpenPopover] = React.useState<boolean>(false)
  const { currentPolicyPeriods, periodIndex } = usePolicyPeriods()

  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const itemRef = React.useRef<HTMLDivElement>(null)

  const {
    access: { locale },
    company: { subscription },
  } = useCurrentSession()

  useUnsafeEffect(() => {
    if (shouldScrollIntoView && itemRef.current) {
      itemRef.current.scrollIntoView()
    }
  })

  const employeeForms = store.formsFor(employee, effectiveOn)
  const supplementsAreOpen = employeeExpansionState?.[employee.id]
  const hasPendingSupplementForms = employeeForms.some(isSupplementFormInvalid)

  useUnsafeEffect(() => {
    if (
      !loadingSupplements &&
      !supplementsAreOpen &&
      hasPendingSupplementForms &&
      toggleEmployeeExpansion
    ) {
      toggleEmployeeExpansion(employee.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingSupplements])

  const { supplementPermissions } = useStatusAuthorizations()

  // Why initialize this way to overwrite the value right after
  let periodStart = effectiveOn
  const policyPeriod = currentPolicyPeriods[periodIndex]
  periodStart = stringToDate(policyPeriod?.attributes.starts_on, 'backend')

  const closeModal = () => {
    setModalOpen(false)
  }

  const openModal = () => {
    setModalOpen(true)
  }

  const canEditEmployeeSupplements =
    supplementPermissions[employee.attributes.id]?.canEditEmployeeSupplements

  const canViewObservations = employee.policy.can('readUserPayrollObservations')

  const policyPeriodId = useUnsafeMemo(
    () => (currentPolicyPeriods.length > 0 ? currentPolicyPeriods[periodIndex]?.id : null), // eslint-disable-next-line react-hooks/exhaustive-deps
    [periodIndex]
  )

  const currentPeriodTotalAmount: number = employeeForms.reduce((total, supplement) => {
    const employeeLegalEntity = supplement.employee && employeeLegalEntityId(supplement.employee)
    const taxonomyId = supplement.fields.contracts_taxonomy_id?.value
    const taxonomy = taxonomies.find(({ id }) => id === Number(taxonomyId))
    const taxonomyLegalEntity = taxonomy?.legalEntityId

    if (supplement.supplementData.unit === 'money' && employeeLegalEntity === taxonomyLegalEntity) {
      return total + supplement.supplementData.amount_in_cents
    } else {
      return total
    }
  }, 0)

  const legalEntity = employeeLegalEntity(employee)
  const employeeCurrentPeriodTotalAmount = () => {
    return MoneyWithCurrency({
      amount: Number(currentPeriodTotalAmount),
      currency: legalEntity.attributes.currency,
      locale,
    })
  }

  const previousPeriodEmployeeTotalAmount = totalForPreviousPeriod[employee.id] ?? undefined

  const amount = employeeCurrentPeriodTotalAmount()
  const employeeCurrency = legalEntity.attributes.currency
  const buildEmptyObservationForm = () => {
    return store.createEmptyObservationForm(employee, periodStart, policyPeriodId)
  }

  const observationForm = store.observationFor(employee) || buildEmptyObservationForm()
  const savedObservation = observations.find({ employee_id: employee.id })

  const observationAvailable =
    Boolean(savedObservation) || !!observationForm?.get('observation_text').originalValue

  const observationText = savedObservation?.get('observation_text')

  useUnsafeEffect(() => {
    if (observationText) {
      observationForm?.get('observation_text')?.set(observationText)
    }
  }, [hover, observationForm, observationText])

  const goToEmployeeContract = () => {
    navigate(
      navigation.employees.id.contractVersions.fullPath({
        id: employee.id.toString(),
      })
    )
  }

  const options: Option[] = [
    {
      onClick: goToEmployeeContract,
      title: i18n.t('handoffs.supplements.view_agreement'),
      icon: 'file-filled',
    },
  ]
  if (canViewObservations) {
    options.push({
      onClick: openModal,
      title: observationAvailable
        ? i18n.t('handoffs.supplements.edit_note')
        : i18n.t('handoffs.supplements.create_note'),
      icon: 'comment',
    })
  }

  const getNumberSupplements = (numberOfSupplements: number) => {
    if (numberOfSupplements <= 0) return null

    return (
      <Box flexDirection="row" gap="s8">
        {numberOfSupplements > 0 && (
          <Tag size="normal" type="neutral">
            {numberOfSupplements}
          </Tag>
        )}

        <Box minWidth={'s140'}>
          <Box alignItems="flexStart" height="full">
            <Box flexDirection="row" alignItems="center" gap="s6" aria-label="amount">
              <Text.Normal weight="normal" color="grey1000" size="300">
                {amount ? amount : 0}
              </Text.Normal>
              {typeof previousPeriodEmployeeTotalAmount === 'number' && (
                <Box>
                  <CompareText
                    amount={Math.round(
                      currentPeriodTotalAmount - previousPeriodEmployeeTotalAmount
                    )}
                    currency={employeeCurrency}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    )
  }

  const renderSupplements = () => {
    if (!loadingSupplements) {
      if (supplementsAreOpen) {
        return children({
          isLast,
          hover,
        })
      }
    }
    return null
  }

  function handleSupplementCreationModalToggle(newPopoverState: boolean) {
    setIsSupplementCreationModalOpen(newPopoverState)
  }

  return (
    <Box
      ref={itemRef}
      className={styles.item}
      onMouseEnter={toggleHover(true)}
      onMouseLeave={toggleHover(false)}
      aria-label={i18n.t('payroll_bundle.accessibility.supplement_item')}
    >
      <Box
        width="full"
        flexDirection="row"
        alignItems="center"
        gap="s16"
        justifyContent="spaceBetween"
        border={{
          top: {
            width: 's1',
            style: 'solid',
            color: 'grey300',
          },
        }}
        {...(employeeForms.length <= 0 && { paddingLeft: 's48' })}
        paddingY="s16"
        paddingRight="s24"
      >
        <Box flexDirection="row" justifyContent="flexStart" alignItems="center" width="s400">
          {employeeForms.length > 0 && (
            <Box
              paddingX={'s12'}
              onClick={() => {
                if (toggleEmployeeExpansion) {
                  toggleEmployeeExpansion(employee.id)
                }
              }}
              aria-label="toggle-expansion"
            >
              <Icon.Medium icon={supplementsAreOpen ? 'chevron-up' : 'chevron-down'} />
            </Box>
          )}

          <EmployeeHeader
            employee={employee}
            estimatedSalary={estimatedSalary}
            observationAvailable={observationAvailable}
            observationText={observationText}
            hover={hover}
            openModal={openModal}
          />
        </Box>

        <Box alignItems="flexStart" justifyContent="flexStart" flexGrow width={'s360'}>
          <Box
            flexDirection="column"
            alignItems="flexStart"
            width="full"
            aria-label={'supplements-info'}
          >
            {getNumberSupplements(employeeForms.length)}
          </Box>
        </Box>
        <Box
          gap="s8"
          flexDirection="row"
          alignItems="center"
          justifyContent="flexEnd"
          width={'s120'}
        >
          {canEditEmployeeSupplements && (
            <PopoverMenu
              type="list"
              open={isSupplementCreationModalOpen}
              onClick={handleSupplementCreationModalToggle}
            >
              <PopoverMenu.Trigger>
                <ExpiredTrialActionChecker
                  subscription={subscription}
                  onClick={() => {
                    if (toggleEmployeeExpansion && !supplementsAreOpen)
                      toggleEmployeeExpansion(employee.id)
                  }}
                >
                  <Button
                    type="positive"
                    aria-label={i18n.t('payroll_bundle.supplements.create_supplement_aria_label')}
                    iconName="add"
                  ></Button>
                </ExpiredTrialActionChecker>
              </PopoverMenu.Trigger>
              <PopoverMenu.Content width={'s400'} side="bottom" align="end">
                <SupplementCreateContent
                  store={store}
                  taxonomies={taxonomies}
                  employee={employee}
                  effectiveOn={effectiveOn}
                  hover={hover}
                  policyPeriodId={policyPeriodId}
                  handleSupplementCreationModalToggle={handleSupplementCreationModalToggle}
                  toggleTaxonomyCreationModal={toggleTaxonomyCreationModal}
                />
              </PopoverMenu.Content>
            </PopoverMenu>
          )}

          {!hover && (
            <Button
              type="secondary"
              onClick={() => setOpenPopover(!openPopover)}
              iconName="ellipsis"
              aria-label="supplement options"
            />
          )}

          {hover && (
            <PopoverMenu type="list">
              <PopoverMenu.Trigger>
                <Button
                  type="secondary"
                  onClick={() => setOpenPopover(!openPopover)}
                  iconName="ellipsis"
                  aria-label="supplement options"
                />
              </PopoverMenu.Trigger>
              <PopoverMenu.Content side="bottom" align="end">
                {options.map(({ title, onClick, icon }, index) => (
                  <PopoverMenu.TouchableOption
                    key={index}
                    title={title}
                    onClick={onClick}
                    icon={icon}
                  />
                ))}
              </PopoverMenu.Content>
            </PopoverMenu>
          )}
        </Box>
      </Box>

      {renderSupplements()}

      {modalOpen && (
        <ObservationModal
          onClose={closeModal}
          employee={employee}
          form={observationForm}
          savedObservation={savedObservation}
        />
      )}
    </Box>
  )
}

export default observer(SupplementsItem)
